import {isRefractoryDeleted} from '@domain/refractory-product-catalog'
import {
  kilnCalcinerOptions,
  kilnPreheaterOptions,
  kilnTechnologyOptions
} from '@settings/modules/plant-setup/types'
import {getVisibleIds, groupByName, pruneSelectedIds} from '@widgets/report-filters'
import {createContext, useContext, useMemo, useState} from 'react'
import {FormProvider, useForm, useWatch} from 'react-hook-form'

import {useExportReport, useGenerateReport} from '../hooks/mutations'

import {useRefractoriesQuery} from '@refra/modules/common/hooks/queries'
import {UpmHierarchyNode} from '@refra/modules/common/types'
import {RefractoryClassificationDto} from '@refra/modules/refra-catalog/types'
import {
  collectDepartmentsWithPlant,
  matchesAssetConfigFilter,
  parseHierarchy
} from '@refra/modules/refra-flexible-report/helpers'
import {
  AllowedRefractoryProductDataNames,
  AllowedUpmLevels,
  AssetConfigFiltersState,
  AssetConfigMap,
  AssetConfigurationDataDto,
  FiltersFormValues,
  HeatZones,
  RefractoryManufacturerDto,
  MateriaInstallationReportRequest,
  MateriaInstallationReportResponse,
  UpmDepartmentWithPlant,
  UpmFilterValue
} from '@refra/modules/refra-flexible-report/types'

interface FiltersContextValue {
  upmFilterValues: Record<AllowedUpmLevels, UpmFilterValue[] | UpmDepartmentWithPlant[]>
  assetConfigFilterValues: AssetConfigFiltersState
  refractoryManufacturerFilterValues: {label: string; value: string}[]
  commitManufacturerFilters: (manufacturerIds: string[]) => void
  refractoryProductFilterValues: Record<
    AllowedRefractoryProductDataNames,
    {label: string; value: string}[]
  >
  isFetchingProducts: boolean
  kilnHeatZoneFilterValues: HeatZones
  syncUpmFilters: (level: AllowedUpmLevels, upmIds: string[]) => void
  filteredKilns: UpmDepartmentWithPlant[]
  report: MateriaInstallationReportResponse | undefined
  isFetching: boolean
  generateReport: (
    payload: MateriaInstallationReportRequest
  ) => Promise<MateriaInstallationReportResponse>
  isExporting: boolean
  exportReport: (payload: MateriaInstallationReportRequest) => Promise<Blob>
}

const FiltersContext = createContext<FiltersContextValue | undefined>(undefined)

export const useFiltersContext = () => {
  const context = useContext(FiltersContext)
  if (!context) throw new Error('useFiltersContext must be used inside FiltersProvider')
  return context
}

const assetConfigFilterOptions: AssetConfigFiltersState = {
  calciner: kilnCalcinerOptions,
  preheater: kilnPreheaterOptions,
  separator: kilnTechnologyOptions
}

export const defaultFiltersFormValues: FiltersFormValues = {
  searchFilter: '',
  upmFilters: {
    ManagementArea: [],
    Country: [],
    Plant: [],
    Department: []
  },
  assetConfigFilters: {
    calciner: [],
    preheater: [],
    separator: []
  },
  refractoryManufacturerFilters: [],
  refractoryProductFilters: {
    product: [],
    classification: []
  },
  kilnHeatZoneFilters: [],
  feedingDateFilter: {from: null, to: null}
}

interface FiltersProviderProps {
  children: React.ReactNode
  upmHierarchyData: {hierarchy: UpmHierarchyNode[]}
  assetConfigData: AssetConfigurationDataDto[]
  refraManufacturersData: RefractoryManufacturerDto[]
  refractoryClassificationsData?: RefractoryClassificationDto[]
  heatZones?: {id: string; enumValue: string}[]
}

export const FiltersProvider: React.FC<FiltersProviderProps> = ({
  children,
  upmHierarchyData,
  assetConfigData,
  refraManufacturersData,
  refractoryClassificationsData,
  heatZones
}) => {
  const form = useForm<FiltersFormValues>({
    defaultValues: defaultFiltersFormValues
  })
  const [committedManufacturerIds, setCommittedManufacturerIds] = useState<number[]>([])
  const {data: refractoriesData, isFetching: isFetchingProducts} = useRefractoriesQuery({
    manufacturerIds: committedManufacturerIds,
    enabled: committedManufacturerIds.length > 0
  })

  const upmFilters = useWatch({control: form.control, name: 'upmFilters'})
  const assetConfigFilters = useWatch({control: form.control, name: 'assetConfigFilters'})
  const searchFilter = useWatch({control: form.control, name: 'searchFilter'})
  const refractoryProductFilters = useWatch({
    control: form.control,
    name: 'refractoryProductFilters'
  })

  const refractoryManufacturerFilterValues = useMemo(
    () =>
      refraManufacturersData.map((manufacturer) => ({
        label: manufacturer.name,
        value: String(manufacturer.id)
      })),
    [refraManufacturersData]
  )

  const commitManufacturerFilters = (manufacturerIds: string[]) => {
    setCommittedManufacturerIds(manufacturerIds.map(Number))
  }

  const refractoryClassificationFilterValues = useMemo(() => {
    return (
      refractoryClassificationsData?.map(({id, class: classificationClass, classification}) => ({
        label: `${classificationClass} ${classification}`,
        value: String(id)
      })) ?? []
    )
  }, [refractoryClassificationsData])

  const refractoryProductFilterValues = useMemo(() => {
    const refractories =
      refractoriesData?.refractories.filter((refractory) => !isRefractoryDeleted(refractory)) ?? []
    const productsFilteredByClassification =
      refractoryProductFilters.classification.length > 0
        ? refractories.filter((product) =>
            refractoryProductFilters.classification.includes(String(product.classificationGroup.id))
          )
        : refractories
    return {
      product: productsFilteredByClassification.map((product) => ({
        label: product.name,
        value: String(product.id)
      })),
      classification: refractoryClassificationFilterValues
    }
  }, [
    refractoriesData,
    refractoryProductFilters.classification,
    refractoryClassificationFilterValues
  ])

  const upmFilterValues = useMemo(() => {
    const {managementAreas, countries, plants, departments} = parseHierarchy(
      upmHierarchyData?.hierarchy,
      upmFilters
    )

    return {
      ManagementArea: groupByName(managementAreas),
      Country: groupByName(countries),
      Plant: groupByName(plants),
      Department: collectDepartmentsWithPlant(plants).filter((department) =>
        departments.includes(department.upmId)
      )
    }
  }, [upmHierarchyData, upmFilters])

  const syncUpmFilters = (level: AllowedUpmLevels, upmIds: string[]) => {
    const currentUpmFilters = form.getValues('upmFilters')
    const newFilters = {...currentUpmFilters, [level]: upmIds}
    const {managementAreas, countries, plants, departments} = parseHierarchy(
      upmHierarchyData?.hierarchy,
      newFilters
    )

    form.setValue('upmFilters', {
      ManagementArea: pruneSelectedIds(
        newFilters.ManagementArea,
        getVisibleIds(groupByName(managementAreas))
      ),
      Country: pruneSelectedIds(newFilters.Country, getVisibleIds(groupByName(countries))),
      Plant: pruneSelectedIds(newFilters.Plant, getVisibleIds(groupByName(plants))),
      Department: pruneSelectedIds(newFilters.Department, departments)
    })
  }

  const assetConfigMapped = useMemo(
    () =>
      assetConfigData.reduce<AssetConfigMap>((acc, item) => {
        acc[item.metadata.typeUpmId] = {
          separator:
            item.payload.productionData.find((item) => item.name === 'separator')?.value ??
            undefined,
          calciner:
            item.payload.productionData.find((item) => item.name === 'calciner')?.value ??
            undefined,
          preheater:
            item.payload.productionData.find((item) => item.name === 'preheater')?.value ??
            undefined
        }
        return acc
      }, {}),
    [assetConfigData]
  )

  const kilnHeatZoneFilterValues = useMemo(() => {
    return heatZones?.map((zone) => ({enumValue: zone.enumValue})) ?? []
  }, [heatZones])

  const filteredKilns = useMemo(() => {
    const isAssetConfigFiltered = Object.values(assetConfigFilters).some(
      (value) => value !== undefined && value.length > 0
    )

    let kilns = upmFilterValues.Department

    if (isAssetConfigFiltered) {
      kilns = kilns.filter((department) => {
        const assetConfigValues = assetConfigMapped[department.upmId]
        if (!assetConfigValues) return false
        return matchesAssetConfigFilter(assetConfigValues, assetConfigFilters)
      })
    }

    const query = searchFilter.trim().toLowerCase()
    if (query) {
      kilns = kilns.filter(
        (kiln) =>
          kiln.plantName?.toLowerCase().includes(query) || kiln.value?.toLowerCase().includes(query)
      )
    }

    return kilns
  }, [upmFilterValues, assetConfigFilters, assetConfigMapped, searchFilter])

  const {report, isFetching, generateReport} = useGenerateReport()
  const {isExporting, exportReport} = useExportReport()

  return (
    <FormProvider {...form}>
      <FiltersContext.Provider
        value={{
          upmFilterValues,
          assetConfigFilterValues: assetConfigFilterOptions,
          refractoryManufacturerFilterValues,
          commitManufacturerFilters,
          refractoryProductFilterValues,
          isFetchingProducts,
          kilnHeatZoneFilterValues,
          syncUpmFilters,
          filteredKilns,
          report,
          isFetching,
          generateReport,
          isExporting,
          exportReport
        }}
      >
        {children}
      </FiltersContext.Provider>
    </FormProvider>
  )
}
